import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { AuthImage, AuthVideo } from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { Send, DollarSign, MessageSquare, CheckCircle2, Star, ArrowLeft, Tag, Calendar, Clock, BookmarkPlus, FileText, Trash2, AlertTriangle, ShieldAlert, Lock, Zap, Eye, MapPin, Phone, User, Car, Wrench, ClipboardList, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function IssueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [issue, setIssue] = useState(null);
  const [bids, setBids] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockedLead, setLockedLead] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessage, setChatMessage] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [acceptingBid, setAcceptingBid] = useState(null);

  // Quote form state
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");
  const [quoteTimeValue, setQuoteTimeValue] = useState("");
  const [quoteTimeUnit, setQuoteTimeUnit] = useState("days");
  const [submittingQuote, setSubmittingQuote] = useState(false);
  // New enhanced fields
  const [confidenceLevel, setConfidenceLevel] = useState("");
  const [partsCost, setPartsCost] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [diagnosticNotes, setDiagnosticNotes] = useState("");

  const isSandbox = !!location.state?.isSandbox;
  const isShop = user?.role === "shop";
  const isOwner = user?.role === "owner";

  // Slot reset countdown helper for free/pro shops
  const getSlotResetText = () => {
    if (!issue?.slot_resets_at) return null;
    const tier = user?.subscription_tier || "free";
    const isFoundingActive = user?.is_founding_member && user?.founding_days_remaining > 0;
    if (tier === "premium" || isFoundingActive) return null;
    if (tier !== "free" && tier !== "pro") return null;
    const diff = new Date(issue.slot_resets_at).getTime() - Date.now();
    if (diff <= 0) return "Resetting now";
    const days = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ${hrs}h`;
    const mins = Math.floor((diff % 3600000) / 60000);
    return hrs > 0 ? `${hrs} hour${hrs > 1 ? "s" : ""} ${mins}m` : `${mins} minute${mins > 1 ? "s" : ""}`;
  };

  // Normalize issue fields from backend format to frontend format
  const normalizeIssue = (data) => {
    return {
      ...data,
      car_make: data.car_make || data.vehicle_make,
      car_model: data.car_model || data.vehicle_model,
      car_year: data.car_year || data.vehicle_year,
      distance_mi: data.distance_mi ?? data.distance_miles,
      // Build media array from file_paths if media not already present
      media: data.media || (data.file_paths || []).map(fp => {
        const ext = fp.includes(".") ? fp.split(".").pop().toLowerCase() : "";
        const type = ["mp4", "mov", "avi", "webm"].includes(ext) ? "video" : "image";
        return { type, path: fp };
      }),
    };
  };

  const load = useCallback(async () => {
    // Sandbox/demo leads: use data passed via route state, skip API fetch
    if (isSandbox && location.state?.lead) {
      const lead = location.state.lead;
      setIssue(normalizeIssue(lead));
      setBids([]);
      setLoading(false);
      return;
    }
    try {
      const issueRes = await api.get(`/issues/${id}`);
      // Handle locked lead response (200 with locked: true)
      if (issueRes.data?.locked === true) {
        setLockedLead(issueRes.data);
        setLoading(false);
        return;
      }
      const bidsRes = await api.get(`/issues/${id}/bids`);
      setIssue(normalizeIssue(issueRes.data));
      setBids(bidsRes.data);
      try {
        const bk = await api.get(`/bookings/by-issue/${id}`);
        setBooking(bk.data);
      } catch (_) { setBooking(null); }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        // Genuinely missing lead — leave issue as null so "Lead not found" renders
      } else {
        toast.error("Failed to load details");
      }
    } finally {
      setLoading(false);
    }
  }, [id, isSandbox, location.state]);

  useEffect(() => { load(); }, [load]);

  // Mark bids as seen when owner opens the issue detail
  useEffect(() => {
    if (isOwner && id && !isSandbox) {
      api.post(`/issues/${id}/bids/mark-seen`).catch(() => {});
    }
  }, [isOwner, id, isSandbox]);


  // Load unread counts for chat badges on bid tiles
  useEffect(() => {
    if (!id || isSandbox) return;
    const loadUnreadCounts = async () => {
      try {
        if (isOwner) {
          // Owner: get per-shop thread unread counts
          const res = await api.get(`/issues/${id}/chat/threads`);
          const counts = {};
          (res.data?.threads || []).forEach(t => {
            if (t.unread_count > 0) counts[t.shop_id] = t.unread_count;
          });
          setUnreadCounts(counts);
        } else {
          // Shop: get own unread count for this issue
          const res = await api.get(`/issues/${id}/chat/unread-count`);
          setUnreadCounts({ _self: res.data?.unread_count || 0 });
        }
      } catch { /* silent */ }
    };
    loadUnreadCounts();
  }, [id, isOwner, isSandbox]);

  // Accept a quote
  const handleAcceptQuote = async (bidId) => {
    if (!window.confirm("Accept this quote? The shop will be notified and other quotes will be declined.")) return;
    setAcceptingBid(bidId);
    try {
      await api.post(`/bids/${bidId}/accept`);
      toast.success("Quote accepted! The shop has been notified.");
      load(); // Refresh data
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept quote");
    } finally {
      setAcceptingBid(null);
    }
  };

  // Compute parts + labor subtotal
  const partsLaborSubtotal = (() => {
    const p = parseFloat(partsCost) || 0;
    const l = parseFloat(laborCost) || 0;
    if (p > 0 && l > 0) return p + l;
    return null;
  })();

  // Submit quote handler
  const handleSubmitQuote = async () => {
    if (!confidenceLevel) {
      toast.error("Please select a confidence level");
      return;
    }
    if (confidenceLevel !== "needs_inspection" && (!quoteAmount || parseFloat(quoteAmount) <= 0)) {
      toast.error("Please enter a valid quote amount");
      return;
    }
    if (!quoteMessage.trim()) {
      toast.error("Please include a message with your quote");
      return;
    }
    setSubmittingQuote(true);
    try {
      await api.post("/bids", {
        issue_id: issue.issue_id || id,
        amount: parseFloat(quoteAmount),
        message: quoteMessage.trim(),
        estimated_time: quoteTimeValue ? parseFloat(quoteTimeValue) : 1,
        estimated_time_unit: quoteTimeUnit,
        confidence_level: confidenceLevel,
        parts_cost: partsCost ? parseFloat(partsCost) : null,
        labor_cost: laborCost ? parseFloat(laborCost) : null,
        diagnostic_notes: diagnosticNotes.trim() || null,
      });
      toast.success("Quote submitted successfully!");
      setBidDialogOpen(false);
      setQuoteAmount("");
      setQuoteMessage("");
      setQuoteTimeValue("");
      setConfidenceLevel("");
      setPartsCost("");
      setLaborCost("");
      setDiagnosticNotes("");
      // Reload to reflect the new state
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Failed to submit quote");
      }
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Chat helpers — deterministic thread_id matching backend logic
  const openChat = (target) => {
    setChatTarget(target);
    setChatOpen(true);
    setChatMessages([]);
    // Mark messages as read and clear badge
    const shopId = target?.shopId;
    if (id) {
      const params = shopId ? { shop_id: shopId } : {};
      api.patch(`/issues/${id}/chat/read`, null, { params }).catch(() => {});
    }
    if (shopId) {
      setUnreadCounts(prev => { const n = { ...prev }; delete n[shopId]; return n; });
    } else {
      setUnreadCounts(prev => ({ ...prev, _self: 0 }));
    }
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim() || sendingChat) return;
    const msgText = chatMessage.trim();
    setSendingChat(true);
    try {
      const payload = { message: msgText };
      // Owner must specify which shop thread; shop is auto-resolved by backend
      if (user?.role === "owner" && chatTarget?.shopId) {
        payload.shop_id = chatTarget.shopId;
      }
      const postRes = await api.post(`/issues/${id}/chat`, payload);
      setChatMessage("");
      // Optimistic UI: immediately show sent message so sender sees it
      if (postRes.data) {
        setChatMessages((prev) => [...prev, postRes.data]);
      }
      // Then re-fetch full list to sync with server (cache-bust with timestamp)
      try {
        const params = user?.role === "owner" && chatTarget?.shopId ? { shop_id: chatTarget.shopId } : {};
        params._t = Date.now();
        const res = await api.get(`/issues/${id}/chat`, { params });
        if (res.data?.messages?.length) {
          setChatMessages(res.data.messages);
        }
      } catch (_) { /* re-fetch failed; optimistic update already shows the message */ }
    } catch (e) {
      const detail = e?.response?.data?.detail; toast.error(typeof detail === "string" ? detail : "Failed to send message");
    } finally {
      setSendingChat(false);
    }
  };

  // Load messages when chat drawer opens + poll every 5s
  useEffect(() => {
    if (!chatOpen || !id) return;
    const fetchChatMessages = async () => {
      try {
        const params = user?.role === "owner" && chatTarget?.shopId ? { shop_id: chatTarget.shopId } : {};
        params._t = Date.now();
        const res = await api.get(`/issues/${id}/chat`, { params });
        setChatMessages(res.data?.messages || []);
      } catch (e) { /* ignore */ }
    };
    fetchChatMessages();
    const interval = setInterval(fetchChatMessages, 5000);
    return () => clearInterval(interval);
  }, [chatOpen, id, chatTarget, user]);

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF3B30]"></div></div>;
  // Locked lead: show upgrade screen for gated free-tier shops
  if (lockedLead) return (
    <div className="min-h-screen bg-[#09090B] text-white flex flex-col items-center justify-center p-8">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30">
          <Lock className="w-10 h-10 text-[#FF3B30]" />
        </div>
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight mb-2">{lockedLead.reason === "pending_approval" ? "Pending Approval" : "Premium Lead"}</h1>
          <p className="text-zinc-400 text-sm leading-relaxed">{lockedLead.reason === "pending_approval"
            ? (lockedLead.message || "Your shop is pending approval. Quoting, chat and bookings unlock once an admin approves your application.")
            : "Upgrade your plan to view full lead details and submit a quote."}</p>
        </div>
        {lockedLead.lead_preview && (
          <div className="border border-white/10 bg-[#121214] rounded-lg p-4 space-y-2 text-sm">
            {lockedLead.lead_preview.miles_away != null && (
              <div className="flex items-center justify-center gap-2 text-zinc-300">
                <MapPin className="w-4 h-4 text-[#FF3B30]" />
                <span>{lockedLead.lead_preview.miles_away} miles away</span>
              </div>
            )}
            {lockedLead.lead_preview.miles_away == null && lockedLead.lead_preview.distance_label && (
              <div className="flex items-center justify-center gap-2 text-zinc-300">
                <MapPin className="w-4 h-4 text-[#FF3B30]" />
                <span>{lockedLead.lead_preview.distance_label} away</span>
              </div>
            )}
            {lockedLead.lead_preview.expires_at && (
              <div className="flex items-center justify-center gap-2 text-zinc-300">
                <Clock className="w-4 h-4 text-zinc-500" />
                <span>Expires: {new Date(lockedLead.lead_preview.expires_at).toLocaleDateString()}</span>
              </div>
            )}
            {lockedLead.lead_preview.category && (
              <div className="flex items-center justify-center gap-2 text-zinc-300">
                <Tag className="w-4 h-4 text-zinc-500" />
                <span>{lockedLead.lead_preview.category}</span>
              </div>
            )}
          </div>
        )}
        {lockedLead.reason === "pending_approval" ? (
          <button
            onClick={() => navigate("/shop/apply")}
            className="w-full py-3.5 bg-[#FFB800] hover:bg-[#FFB800]/90 text-black text-sm font-black uppercase tracking-widest rounded transition-colors"
            data-testid="pending-view-application"
          >
            View My Application
          </button>
        ) : (
        <button
          onClick={() => navigate("/upgrade")}
          className="w-full py-3.5 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white text-sm font-black uppercase tracking-widest rounded transition-colors"
        >
          Upgrade Plan
        </button>
        )}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors mx-auto"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Leads
        </button>
      </div>
    </div>
  );

  if (!issue) return (
    <div className="min-h-screen bg-[#09090B] text-white p-8 text-center flex flex-col items-center justify-center">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Leads
      </button>
      <p className="text-zinc-300">Lead not found</p>
    </div>
  );

  // Confidence level options
  const confidenceOptions = [
    { value: "high", emoji: "🟢", label: "HIGH CONFIDENCE", desc: "I've seen this before, parts and labor are well-scoped" },
    { value: "moderate", emoji: "🟡", label: "MODERATE", desc: "Likely diagnosis, may need further inspection" },
    { value: "needs_inspection", emoji: "🔴", label: "NEEDS INSPECTION", desc: "Can't confirm without seeing the vehicle in person" },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-[#E2E8F0] pb-20">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isSandbox && (
          <div className="mb-6 p-3 bg-[#FFB800]/10 border border-[#FFB800]/30 flex items-center gap-3">
            <Zap className="text-[#FFB800] w-4 h-4 flex-shrink-0" />
            <p className="text-xs font-bold text-[#FFB800] uppercase tracking-widest">Sandbox Lead — Demo Data</p>
          </div>
        )}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-500 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Lead Intel */}
          <div className="lg:col-span-2 space-y-8">
            {/* Vehicle & Header */}
            <div className="bg-[#121214] border border-white/5 p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h1 className="text-3xl font-black tracking-tight uppercase font-heading">{issue.car_year} {issue.car_make} {issue.car_model}</h1>
                  <div className="flex items-center gap-4 mt-2 text-zinc-500 font-mono text-xs uppercase">
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {issue.distance_mi != null ? `${issue.distance_mi.toFixed(1)} miles` : (issue.distance_label || "Nearby")}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Posted {new Date(issue.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {issue.urgent && <div className="bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] font-black px-3 py-1 border border-[#FF3B30]/20 uppercase tracking-widest">Urgent</div>}
              </div>

              {/* Slot reset countdown for free/pro shops */}
              {isShop && issue.has_quoted && getSlotResetText() && (
                <div className="mt-4 mb-2 bg-emerald-500/5 border border-emerald-500/10 px-4 py-3 rounded">
                  <p className="text-xs text-emerald-400/80">
                    <span className="font-semibold">🔓 Your slot for this lead resets in {getSlotResetText()}</span>
                    <span className="text-zinc-500 ml-1">— when it resets, a new lead will become viewable to you automatically.</span>
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-1">Slot also resets if this post is closed or another shop's quote is accepted.</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Vehicle Info */}
                {(issue.car_make || issue.car_model || issue.car_year || issue.fuel_type) && (
                  <div>
                    <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-3">Vehicle Information</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {issue.car_year && (
                        <div className="bg-zinc-900/50 border border-white/5 p-3">
                          <p className="text-[10px] text-zinc-600 uppercase font-mono mb-1">Year</p>
                          <p className="text-sm font-bold">{issue.car_year}</p>
                        </div>
                      )}
                      {issue.car_make && (
                        <div className="bg-zinc-900/50 border border-white/5 p-3">
                          <p className="text-[10px] text-zinc-600 uppercase font-mono mb-1">Make</p>
                          <p className="text-sm font-bold">{issue.car_make}</p>
                        </div>
                      )}
                      {issue.car_model && (
                        <div className="bg-zinc-900/50 border border-white/5 p-3">
                          <p className="text-[10px] text-zinc-600 uppercase font-mono mb-1">Model</p>
                          <p className="text-sm font-bold">{issue.car_model}</p>
                        </div>
                      )}
                      {issue.fuel_type && (
                        <div className="bg-zinc-900/50 border border-white/5 p-3">
                          <p className="text-[10px] text-zinc-600 uppercase font-mono mb-1">Fuel</p>
                          <p className="text-sm font-bold capitalize">{issue.fuel_type}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Service Category */}
                {(issue.category || issue.ai_category) && (
                  <div>
                    <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-3">Service Category</h3>
                    <span className="inline-block bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-bold px-3 py-1.5 border border-[#FF3B30]/20 uppercase tracking-wider">
                      {issue.category || issue.ai_category}
                    </span>
                  </div>
                )}

                {/* Service Description */}
                <div>
                  <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-3">Service Description</h3>
                  <p className="text-zinc-400 leading-relaxed">{issue.description}</p>
                </div>

                {/* Owner Contact Info */}
                {issue.owner_name && (
                  <div>
                    <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-3">Car Owner</h3>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 bg-zinc-900/50 border border-white/5 px-4 py-2.5">
                        <User className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm" data-testid="issue-owner-name">{issue.owner_name}</span>
                      </div>
                      {issue.location_approximate && (
                        <span className="text-[11px] text-zinc-500 self-center">First name and phone are shared after the owner accepts your quote.</span>
                      )}
                      {issue.owner_phone && (
                        <a href={`tel:${issue.owner_phone}`} className="flex items-center gap-2 bg-zinc-900/50 border border-white/5 px-4 py-2.5 hover:border-[#FF3B30]/30 transition-colors">
                          <Phone className="w-4 h-4 text-zinc-500" />
                          <span className="text-sm">{issue.owner_phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Location */}
                {issue.location && (
                  <div>
                    <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-3">Location</h3>
                    <div className="flex items-center gap-2 text-zinc-400 text-sm">
                      <MapPin className="w-4 h-4 text-zinc-500" />
                      <span>{issue.location}</span>
                    </div>
                    {issue.location_approximate && (
                      <p className="text-[11px] text-zinc-500 mt-2" data-testid="issue-location-approx-note">
                        Approximate area (about 1 km). The exact location is shared after the owner accepts your quote.
                      </p>
                    )}
                  </div>
                )}

                {/* Photos & Videos */}
                {issue.media && issue.media.length > 0 && (
                  <div>
                    <h3 className="text-[10px] font-mono font-bold text-zinc-600 uppercase tracking-widest mb-4">Photos & Videos</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {issue.media.map((m, i) => (
                        <div key={i} className="aspect-square bg-zinc-900 border border-white/5 overflow-hidden group">
                          {m.type === 'video' ? (
                            <AuthVideo path={m.path || m.url} className="w-full h-full object-cover" />
                          ) : (
                            <AuthImage path={m.path || m.url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* shop-status-access-nov2: accepted shop no longer available */}
          {isOwner && (issue?.accepted_shop_unavailable || booking?.shop_unavailable) && (
            <div className="lg:col-span-2 p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 flex items-start gap-3" data-testid="owner-shop-unavailable">
              <AlertTriangle className="w-5 h-5 text-[#FFB800] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-[#FFB800] uppercase tracking-widest">This shop is currently unavailable</p>
                <p className="text-xs text-zinc-400 mt-0.5">Our team has been notified and will reach out to help with your booking. You can cancel the booking at any time, or contact support.</p>
              </div>
            </div>
          )}

          {/* Owner: Quotes Received */}
          {isOwner && bids.length > 0 && (
            <div className="lg:col-span-2 space-y-0">
              <div className="bg-[#121214] border border-white/5 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-5 h-5 text-[#34C759]" />
                  <h2 className="text-lg font-black uppercase tracking-tight">Quotes Received</h2>
                  <span className="ml-auto text-xs font-mono text-zinc-500">{bids.length} {bids.length === 1 ? "quote" : "quotes"}</span>
                </div>
                <div className="space-y-4">
                  {bids.map((bid) => {
                    const conf = bid.confidence_level;
                    const confDisplay = conf === "high"
                      ? { emoji: "🟢", label: "HIGH CONFIDENCE", color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" }
                      : conf === "moderate"
                      ? { emoji: "🟡", label: "MODERATE", color: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" }
                      : conf === "needs_inspection"
                      ? { emoji: "🔴", label: "NEEDS INSPECTION", color: "text-red-400 border-red-500/30 bg-red-500/10" }
                      : null;
                    const isAccepted = bid.status === "accepted";
                    const isRejected = bid.status === "rejected";
                    return (
                      <div key={bid.bid_id} className={`border p-6 transition-all relative ${isAccepted ? "border-emerald-500/40 bg-emerald-500/5" : isRejected ? "border-white/5 bg-zinc-900/50 opacity-60" : "border-white/10 bg-[#09090B] hover:border-white/20"}`}>
                        {unreadCounts[bid.shop_id || bid.shop?.id || bid.shopId] > 0 && (
                          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-widest animate-pulse">
                            <MessageSquare className="w-3 h-3" />
                            {unreadCounts[bid.shop_id || bid.shop?.id || bid.shopId]} NEW
                          </span>
                        )}
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="font-heading text-base font-bold text-white">{bid.shop_name || "Shop"}</div>
                            {bid.shop_rating > 0 && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-zinc-400">
                                <Star className="w-3 h-3 text-[#FFB800] fill-[#FFB800]" />
                                <span>{bid.shop_rating.toFixed(1)}</span>
                                {bid.shop_rating_count > 0 && <span className="text-zinc-600">({bid.shop_rating_count})</span>}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-0.5">Estimate</div>
                            <div className="font-heading text-2xl font-black text-[#34C759]">${parseFloat(bid.amount || 0).toLocaleString()}</div>
                            {isAccepted && <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">✓ ACCEPTED</span>}
                            {isRejected && <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">DECLINED</span>}
                            {bid.shop_unavailable && <div className="text-[10px] font-black text-[#FFB800] uppercase tracking-widest mt-1" data-testid="shop-unavailable-badge">{bid.shop_unavailable_message || "This shop is currently unavailable."}</div>}
                          </div>
                        </div>

                        {confDisplay && (
                          <div className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 border mb-4 ${confDisplay.color}`}>
                            <span>{confDisplay.emoji}</span> {confDisplay.label}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {bid.parts_cost != null && (
                            <div className="text-sm">
                              <span className="text-zinc-500 text-xs uppercase">Parts</span>
                              <div className="text-white font-mono">${parseFloat(bid.parts_cost).toLocaleString()}</div>
                            </div>
                          )}
                          {bid.labor_cost != null && (
                            <div className="text-sm">
                              <span className="text-zinc-500 text-xs uppercase">Labor</span>
                              <div className="text-white font-mono">${parseFloat(bid.labor_cost).toLocaleString()}</div>
                            </div>
                          )}
                          {(bid.estimated_time || bid.estimated_days) && (
                            <div className="text-sm">
                              <span className="text-zinc-500 text-xs uppercase">Est. Time</span>
                              <div className="text-white">{bid.estimated_time || bid.estimated_days} {bid.estimated_time_unit || "days"}</div>
                            </div>
                          )}
                        </div>

                        {bid.diagnostic_notes && (
                          <div className="mb-4 p-3 bg-zinc-800/50 border border-white/5">
                            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Diagnostic Notes</div>
                            <p className="text-sm text-zinc-300">{bid.diagnostic_notes}</p>
                          </div>
                        )}

                        {bid.message && (
                          <div className="mb-4">
                            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Message from Shop</div>
                            <p className="text-sm text-zinc-300">{bid.message}</p>
                          </div>
                        )}

                        {!isAccepted && !isRejected && bid.status === "pending" && !bid.shop_unavailable && (
                          <button
                            onClick={() => handleAcceptQuote(bid.bid_id)}
                            disabled={acceptingBid === bid.bid_id}
                            className="w-full mt-2 bg-[#34C759] hover:bg-[#34C759]/90 text-black font-black text-xs uppercase tracking-widest py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {acceptingBid === bid.bid_id ? "ACCEPTING..." : "ACCEPT QUOTE"}
                          </button>
                        )}
                        {(issue?.status === "open" || issue?.status === "in_progress") && bids.length > 0 && (
                          <div className="relative w-full mt-2">
                          <button
                            onClick={() => openChat({ shopId: bid.shop_id || bid.shop?.id || bid.shopId, name: bid.shop_name || "Shop", type: "shop" })}
                            className="w-full border border-white/10 text-zinc-300 hover:text-white hover:border-white/30 font-black text-xs uppercase tracking-widest py-3 transition-colors flex items-center justify-center gap-2"
                          >
                            <MessageSquare className="w-4 h-4" />
                            💬 CHAT WITH SHOP
                          </button>
                          {unreadCounts[bid.shop_id || bid.shop?.id || bid.shopId] > 0 && (
                            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[20px] h-5 bg-[#FF3B30] text-white text-[10px] font-black px-1.5 rounded-full shadow-lg">
                              {unreadCounts[bid.shop_id || bid.shop?.id || bid.shopId]}
                            </span>
                          )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {isOwner && bids.length === 0 && !loading && issue?.status === "open" && (
            <div className="lg:col-span-2">
              <div className="border border-dashed border-white/10 p-8 text-center">
                <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-1">No Quotes Yet</div>
                <p className="text-sm text-zinc-400">Your issue is live! Nearby shops can see it now. You'll be notified when quotes come in.</p>
              </div>
            </div>
          )}

          {/* Right: Actions & Status */}
          <div className="space-y-6">
             {isShop && (
               <div className="bg-[#121214] border border-white/5 p-6">
                 <h3 className="text-sm font-bold uppercase tracking-widest mb-6">Lead Actions</h3>
                 {booking ? (
                   <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-center font-bold uppercase tracking-widest text-xs">
                     Lead Secured
                   </div>
                 ) : user?.status !== "approved" ? (
                   <button
                     disabled
                     className="w-full py-4 flex items-center justify-center gap-2 bg-[#FFB800]/10 text-[#FFB800] cursor-not-allowed font-black text-xs uppercase tracking-widest border border-[#FFB800]/30"
                     data-testid="quote-disabled-pending"
                   >
                     <Clock className="w-4 h-4" /> Quoting unlocks after approval
                   </button>
                 ) : issue.has_quoted ? (
                   <button 
                     disabled
                     className="w-full py-4 flex items-center justify-center gap-2 bg-zinc-700/50 text-zinc-500 cursor-not-allowed font-black text-xs uppercase tracking-widest border border-white/5"
                   >
                     <Zap className="w-4 h-4" /> ✓ Quote Sent
                   </button>
                 ) : (
                   <button 
                     onClick={() => setBidDialogOpen(true)} 
                     className="w-full btn-primary py-4 flex items-center justify-center gap-2"
                   >
                     <Zap className="w-4 h-4" /> Submit Lead Quote
                   </button>
                 )}
                 {(issue?.status === "open" || issue?.status === "in_progress") && bids.length > 0 && (
                   <div className="relative w-full mt-3">
                   <button
                     onClick={() => openChat({ shopId: null, name: "Vehicle Owner", type: "owner" })}
                     className="w-full border border-white/10 text-zinc-300 hover:text-white hover:border-white/30 font-black text-xs uppercase tracking-widest py-3 transition-colors flex items-center justify-center gap-2"
                   >
                     <MessageSquare className="w-4 h-4" />
                     💬 CHAT WITH OWNER
                   </button>
                   {unreadCounts._self > 0 && (
                     <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[20px] h-5 bg-[#FF3B30] text-white text-[10px] font-black px-1.5 rounded-full shadow-lg">
                       {unreadCounts._self}
                     </span>
                   )}
                   </div>
                 )}
               </div>
             )}

             {/* Lead Details Summary Card */}
             <div className="bg-[#121214] border border-white/5 p-6">
               <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Lead Summary</h3>
               <div className="space-y-3 text-sm">
                 {issue.car_year && issue.car_make && (
                   <div className="flex items-center gap-2 text-zinc-400">
                     <Car className="w-4 h-4 text-zinc-600" />
                     <span>{issue.car_year} {issue.car_make} {issue.car_model}</span>
                   </div>
                 )}
                 {issue.distance_mi != null && (
                   <div className="flex items-center gap-2 text-zinc-400">
                     <MapPin className="w-4 h-4 text-zinc-600" />
                     <span>{issue.distance_mi.toFixed(1)} miles away</span>
                   </div>
                 )}
                 {issue.distance_mi == null && issue.distance_label && (
                   <div className="flex items-center gap-2 text-zinc-400" data-testid="issue-distance-band">
                     <MapPin className="w-4 h-4 text-zinc-600" />
                     <span>{issue.distance_label} away</span>
                   </div>
                 )}
                 {issue.created_at && (
                   <div className="flex items-center gap-2 text-zinc-400">
                     <Clock className="w-4 h-4 text-zinc-600" />
                     <span>Posted {new Date(issue.created_at).toLocaleDateString()}</span>
                   </div>
                 )}
                 {(issue.category || issue.ai_category) && (
                   <div className="flex items-center gap-2 text-zinc-400">
                     <Tag className="w-4 h-4 text-zinc-600" />
                     <span className="capitalize">{issue.category || issue.ai_category}</span>
                   </div>
                 )}
               </div>
             </div>
          </div>
        </div>
      </main>

      {/* Quote Submission Dialog */}
      <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
        <DialogContent className="bg-[#121214] border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight">Submit Quote</DialogTitle>
            <p className="text-zinc-500 text-sm mt-1">
              {issue.car_year} {issue.car_make} {issue.car_model} — {issue.description?.slice(0, 60)}{issue.description?.length > 60 ? "..." : ""}
            </p>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* 1. Confidence Tag */}
            <div>
              <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-2 block">
                Confidence Level <span className="text-[#FF3B30]">*</span>
              </label>
              <div className="space-y-2">
                {confidenceOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setConfidenceLevel(opt.value)}
                    className={`w-full text-left px-4 py-3 border transition-all duration-200 ${
                      confidenceLevel === opt.value
                        ? opt.value === "high"
                          ? "bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/30"
                          : opt.value === "moderate"
                          ? "bg-yellow-500/10 border-yellow-500/40 ring-1 ring-yellow-500/30"
                          : "bg-red-500/10 border-red-500/40 ring-1 ring-red-500/30"
                        : "bg-zinc-900/50 border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{opt.emoji}</span>
                      <div>
                        <p className={`text-xs font-black uppercase tracking-widest ${
                          confidenceLevel === opt.value ? "text-white" : "text-zinc-300"
                        }`}>{opt.label}</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{opt.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Quote Amount */}
            <div>
              <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block">Quote Estimate Amount ($) {confidenceLevel !== "needs_inspection" && <span className="text-[#FF3B30]">*</span>}</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="0.00"
                  value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)}
                  className="w-full bg-zinc-900 border border-white/10 text-white pl-9 pr-4 py-3 focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
                />
              </div>
            </div>

            {/* 3. Parts + Labor (side by side) */}
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block">Parts Estimate ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={partsCost}
                      onChange={(e) => setPartsCost(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/10 text-white pl-8 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block">Labor Estimate ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={laborCost}
                      onChange={(e) => setLaborCost(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/10 text-white pl-8 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
                    />
                  </div>
                </div>
              </div>
              {partsLaborSubtotal !== null && (
                <p className="text-[10px] text-zinc-500 font-mono mt-2 text-right">
                  Subtotal: <span className="text-zinc-300">${partsLaborSubtotal.toFixed(2)}</span>
                </p>
              )}
            </div>

            {/* 4. Time Estimate */}
            <div>
              <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block">Estimated Time</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  placeholder="1"
                  value={quoteTimeValue}
                  onChange={(e) => setQuoteTimeValue(e.target.value)}
                  className="flex-1 bg-zinc-900 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
                />
                <select
                  value={quoteTimeUnit}
                  onChange={(e) => setQuoteTimeUnit(e.target.value)}
                  className="bg-zinc-900 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>

            {/* 5. Diagnostic Notes */}
            <div>
              <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block flex items-center gap-1.5">
                <ClipboardList className="w-3 h-3" /> Diagnostic Notes
              </label>
              <textarea
                rows={3}
                placeholder="Describe your diagnosis, what parts are needed, and your approach..."
                value={diagnosticNotes}
                onChange={(e) => setDiagnosticNotes(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FF3B30]/50 transition-colors resize-none"
              />
            </div>

            {/* 6. Message to Owner */}
            <div>
              <label className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-1.5 block">Message to Owner <span className="text-[#FF3B30]">*</span></label>
              <textarea
                rows={3}
                placeholder="Introduce yourself, describe your experience with this type of work, and any questions..."
                value={quoteMessage}
                onChange={(e) => setQuoteMessage(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FF3B30]/50 transition-colors resize-none"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <button
              onClick={() => setBidDialogOpen(false)}
              className="px-4 py-2.5 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitQuote}
              disabled={submittingQuote}
              className="btn-primary px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
            >
              {submittingQuote ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {submittingQuote ? "Submitting..." : "Send Quote"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat Drawer */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="right" className="bg-[#121214] border-l border-white/10 text-white w-full sm:max-w-md flex flex-col p-0 [&>button]:hidden">
          <SheetHeader className="px-4 py-4 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center justify-between w-full">
              <SheetTitle className="text-sm font-black uppercase tracking-widest text-white flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#FF3B30]" />
                {chatTarget?.type === "owner" ? "Chat with Owner" : `Chat with ${chatTarget?.name || "Shop"}`}
              </SheetTitle>
              <button
                onClick={() => setChatOpen(false)}
                className="p-1.5 rounded-sm hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <MessageSquare className="w-10 h-10 text-zinc-700 mb-3" strokeWidth={1.2} />
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">No messages yet</p>
                <p className="text-xs text-zinc-600 mt-1">Start the conversation below</p>
              </div>
            ) : (
              chatMessages.map((msg, i) => {
                const isMine = msg.sender_id === user?.user_id;
                const senderLabel = isMine ? "YOU" : (msg.sender_name || (chatTarget?.type === "owner" ? "OWNER" : "SHOP"));
                return (
                  <div key={msg.message_id || i} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[80%]">
                      <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isMine ? "text-right text-blue-400" : "text-left text-zinc-400"}`}>
                        {senderLabel}
                      </div>
                      <div className={`px-3 py-2 text-sm leading-relaxed ${isMine ? "bg-[#2563EB] text-white rounded-t-lg rounded-bl-lg" : "bg-[#1E1E22] text-zinc-200 rounded-t-lg rounded-br-lg border border-white/5"}`}>
                        <p>{msg.message || msg.content || msg.text}</p>
                      </div>
                      {msg.created_at && (
                        <div className={`text-[10px] text-zinc-600 mt-1 ${isMine ? "text-right" : "text-left"}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex-shrink-0 border-t border-white/10 p-4 flex gap-2">
            <input
              type="text"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendChat()}
              placeholder="Type a message..."
              className="flex-1 bg-zinc-900 border border-white/10 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF3B30]/50 transition-colors"
            />
            <button
              onClick={handleSendChat}
              disabled={sendingChat || !chatMessage.trim()}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-black text-xs uppercase tracking-widest px-4 py-2.5 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" />
              Send
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
